import { useState } from 'react';
import { ApiFailure, api } from '../lib/api';

/** 首次登录强制改密。V1 司机的初始密码由管理员当面告知 */
/**
 * 改密码。
 *
 * ★ 两条路都走这一屏:**首次登录被强制改**,以及他自己在「我的」里主动改。
 *   主动改的入口原来根本没有 —— 司机想换密码只能打电话找调度,
 *   而调度那边只能重置成一个初始密码,等于把密码在电话里念一遍。
 */
export function ChangePassword({
  onDone,
  onCancel,
}: {
  onDone: () => void;
  /** 主动改时给一个退路;首次登录强制改的那条路不传 */
  onCancel?: () => void;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < 10;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy || mismatch || tooShort) return;
    setBusy(true);
    setError(null);
    try {
      await api('/auth/password/change', {
        method: 'POST',
        body: { currentPassword: current, newPassword: next },
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiFailure ? err.message : '修改失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-dvh flex flex-col justify-center px-6 bg-slate-50 dark:bg-slate-900">
      {/* ★ 限宽居中 —— 首次登录会被直接送到这一屏,它和登录页要一样宽 */}
      <div className="mx-auto w-full max-w-sm">
        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-1">
          {onCancel ? '修改密码' : '设置新密码'}
        </h1>
        <p className="text-slate-500 mb-6">
          {onCancel ? '改完之后其它设备要重新登录' : '首次登录需要修改初始密码'}
        </p>

        <form onSubmit={submit} className="space-y-4">
          <Field label="当前密码" type="password" value={current} onChange={setCurrent} />
          <Field label="新密码(至少 10 位)" type="password" value={next} onChange={setNext} />
          <Field label="确认新密码" type="password" value={confirm} onChange={setConfirm} />

          {tooShort && <p className="text-sm text-amber-600">密码至少 10 位</p>}
          {mismatch && <p className="text-sm text-red-600">两次输入不一致</p>}
          {error && (
            <div className="rounded-xl bg-red-50 dark:bg-red-950 border border-red-200
                            dark:border-red-900 px-4 py-3 text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy || !current || !next || mismatch || tooShort}
            className="w-full h-14 rounded-xl bg-brand-600 text-white text-lg font-medium
                       disabled:opacity-40 active:bg-brand-700"
          >
            {busy ? '提交中…' : '确认修改'}
          </button>

          {/* ★ 主动改的那条路要能退出去 —— 点进来才发现记不清旧密码是常事 */}
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="w-full h-12 rounded-xl text-slate-500 dark:text-slate-400"
            >
              取消
            </button>
          )}
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  type,
  value,
  onChange,
}: {
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
}) {
  /*
   * ★ label 要**真的连到 input 上**(包起来),不能只是上面摆一行字。
   *   不连的话读屏软件念出来是三个「密码输入框」,分不清哪个是旧的、
   *   哪个是新的 —— 而这一屏一共就三个框,填错一个就提交不了。
   */
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm text-slate-600 dark:text-slate-400">{label}</span>
      <input
        type={type}
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-14 px-4 text-lg rounded-xl border border-slate-300 dark:border-slate-700
                   bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100
                   focus:outline-none focus:ring-2 focus:ring-brand-500"
      />
    </label>
  );
}
